import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/layout/PageTransition';
import SectionHeader from '../components/ui/SectionHeader';
import Reveal from '../components/ui/Reveal';
import Icon from '../components/ui/Icon';
import CTABanner from '../components/sections/CTABanner';
import { SERVICES, ENGAGEMENTS } from '../data/site';

export default function ServicesPage() {
  return (
    <PageTransition>
      <section className="page-hero">
        <div className="container">
          <Reveal><span className="eyebrow"><span className="dot" />Services</span></Reveal>
          <Reveal delay={0.06} as="h1">
            Six practices.<br />One <span className="serif gold">accountable</span> team.
          </Reveal>
          <Reveal delay={0.12}>
            <p className="lede">
              We work as embedded partners — strategy, design, and engineering under one roof so the
              handoffs disappear and the build stays sharp. No subcontractors, no junior bench,
              no surprises.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section--tight">
        <div className="container">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.slug}
              id={s.slug}
              className="svc-detail"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.16, 0.84, 0.44, 1] }}
            >
              <div>
                <div className="svc-detail__num">{String(i + 1).padStart(2, '0')} / 0{SERVICES.length}</div>
                <div style={{ marginTop: 24, width: 56, height: 56, border: '1px solid var(--border-2)', borderRadius: 14, display: 'grid', placeItems: 'center', color: 'var(--gold)', background: 'var(--bg-1)' }}>
                  <Icon name={s.icon} size={24} />
                </div>
              </div>
              <div>
                <h3>{s.title}</h3>
                <p>{s.long}</p>
                <div className="svc-detail__tags">
                  {s.tags.map((t) => <span key={t} className="svc-detail__tag">{t}</span>)}
                </div>
                <div style={{ marginTop: 28 }}>
                  <Link to="/contact" className="btn btn--dark">
                    Talk about a {s.title.split(' ')[0].toLowerCase()} project
                    <Icon name="arrow" size={14} className="arrow" />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeader
            eyebrow="Engagements"
            title="Three ways to work together."
            lede="Most teams start with a Sprint to de-risk the build, then move into a full Build engagement, then a long-term Partner retainer as the product scales."
          />
          <div className="engagement-grid">
            {ENGAGEMENTS.map((e, i) => (
              <motion.div
                key={e.name}
                className={'engagement' + (e.featured ? ' featured' : '')}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 0.84, 0.44, 1] }}
              >
                <span className="engagement__label">
                  {e.featured && <Icon name="check" size={11} />}
                  {e.label}
                </span>
                <div>
                  <div className="engagement__name">{e.name}</div>
                  <div className="engagement__price" style={{ marginTop: 8 }}>
                    {e.price} <span className="unit">/ {e.unit}</span>
                  </div>
                </div>
                <p style={{ color: 'var(--muted)', fontSize: 14 }}>{e.desc}</p>
                <ul className="engagement__features">
                  {e.features.map((f) => <li key={f}>{f}</li>)}
                </ul>
                <Link
                  to="/contact"
                  className={e.featured ? 'btn btn--primary' : 'btn btn--dark'}
                  style={{ marginTop: 'auto', justifyContent: 'center' }}
                >
                  Start a {e.name}
                  <Icon name="arrow" size={14} className="arrow" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </PageTransition>
  );
}
